import React, { useEffect, useState, useRef } from 'react';
import { usePresenceCentralized } from '../helpers/usePresenceApi';
import { CodeMirrorEditorView as MonacoEditor } from '../components/CodeMirrorEditor';
import { CollaboratorCursorOverlay } from './CollaboratorCursorOverlay';
import styles from './CollaboratorCursors.module.css';

interface CollaboratorCursorsProps {
  sessionId: string;
  currentFile: string;
  currentUserId: number;
  editorRef?: React.MutableRefObject<MonacoEditor | null>;
  className?: string;
}

export const CollaboratorCursors: React.FC<CollaboratorCursorsProps> = ({
  sessionId,
  currentFile,
  currentUserId,
  editorRef,
  className,
}) => {
  const { data: presenceData } = usePresenceCentralized(sessionId);
  const [scrollPosition, setScrollPosition] = useState({ top: 0, left: 0 });

  // Force re-render when editor scrolls to update cursor positions
  useEffect(() => {
    const editor = editorRef?.current;
    if (!editor) return;

    const updateScroll = () => {
      setScrollPosition({
        top: editor.getScrollTop(),
        left: editor.getScrollLeft(),
      });
    };

    const disposable = editor.onDidScrollChange(updateScroll);

    // Initial set
    updateScroll();

    return () => {
      disposable.dispose();
    };
  }, [editorRef, editorRef?.current]); // Re-bind if editor instance changes

  // presenceData is an array directly from usePresenceCentralized
  if (!presenceData || presenceData.length === 0) return null;

  // Filter relevant participants
  const activeParticipants = presenceData.filter(
    (p) =>
      p.userId !== currentUserId &&
      p.currentFile === currentFile &&
      p.cursorLine !== null &&
      p.cursorColumn !== null,
  );

  return (
    <div className={`${styles.container} ${className ?? ''}`}>
      {activeParticipants.map((participant) => (
        <CollaboratorCursorOverlay
          key={participant.userId}
          participant={participant}
          editorRef={editorRef}
          scrollTrigger={scrollPosition} // Pass scroll trigger to force update
        />
      ))}
    </div>
  );
};
